import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { X, Bot } from 'lucide-react';
import { createAppAgent } from '../api';

interface CreateAgentModalProps {
  appId: string;
  open: boolean;
  onClose: () => void;
  onCreated?: () => void;
}

export function CreateAgentModal({ appId, open, onClose, onCreated }: CreateAgentModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await createAppAgent(appId, {
        name: name.trim(),
        description: description.trim() || `App agent for ${name.trim()}`,
      });
      setName('');
      setDescription('');
      onCreated?.();
      onClose();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <Card className="w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center gap-2">
              <Bot className="h-4 w-4" /> Create App Agent
            </h3>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Agent Name</label>
              <Input
                placeholder="e.g., Equipment Tracker"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="text-sm"
                autoFocus
              />
            </div>

            <div>
              <label className="text-xs text-muted-foreground block mb-1">Description</label>
              <Textarea
                placeholder="What does this agent do?"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="text-sm"
                rows={3}
              />
            </div>

            <p className="text-xs text-muted-foreground">
              The agent will be created with app-level permissions and automatically added to this app's allowlist.
              Agent ID will be prefixed with your app ID.
            </p>

            {error && <p className="text-xs text-red-500">{error}</p>}

            <div className="flex gap-2 justify-end pt-1">
              <Button type="button" variant="outline" size="sm" onClick={onClose}>Cancel</Button>
              <Button type="submit" size="sm" disabled={loading || !name.trim()}>
                {loading ? 'Creating...' : 'Create Agent'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
